/**
 * AdminSoftDeleteBundleModal — potwierdzenie wycofania pakietu z katalogu (T8.2).
 *
 * Backend: DELETE /api/admin/bundles/:code, body `{reason}` (wymagany, min 1 char).
 * Walidacja po stronie backend: 409 BUNDLE_ALREADY_DELETED, 422 VALIDATION_FAILED.
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export interface AdminSoftDeleteBundleModalProps {
  bundleCode: string;
  bundleName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminSoftDeleteBundleModal({
  bundleCode,
  bundleName,
  onClose,
  onSuccess,
}: AdminSoftDeleteBundleModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = reason.trim();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (trimmed.length === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.delete(`/admin/bundles/${bundleCode}`, { data: { reason: trimmed } });
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const code = axErr.response?.data?.error ?? null;
      if (code === 'BUNDLE_ALREADY_DELETED') {
        setError(t('admin.bundles.softDelete.errors.alreadyDeleted'));
      } else if (axErr.response?.status === 422) {
        setError(t('admin.bundles.softDelete.errors.validation'));
      } else {
        setError(t('admin.bundles.softDelete.errors.generic'));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('admin.bundles.softDelete.title', { name: bundleName })}
      description={t('admin.bundles.softDelete.description')}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.bundles.softDelete.cancel')}
          </Button>
          <Button
            type="submit"
            form="soft-delete-bundle-form"
            variant="danger"
            loading={submitting}
            disabled={submitting || trimmed.length === 0}
          >
            {submitting
              ? t('admin.bundles.softDelete.submitting')
              : t('admin.bundles.softDelete.confirm')}
          </Button>
        </>
      }
    >
      <form
        id="soft-delete-bundle-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
        noValidate
      >
        <p className="text-sm text-text-tertiary">{t('admin.bundles.softDelete.body')}</p>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-secondary">
            {t('admin.bundles.softDelete.reasonLabel')}
          </span>
          <textarea
            className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 dark:border-border-primary dark:bg-bg-tertiary"
            rows={3}
            maxLength={500}
            required
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            placeholder={t('admin.bundles.softDelete.reasonPlaceholder')}
          />
        </label>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
